import { useResponse } from 'contexts/response';
import { useCallback } from 'react';

import { Transaction } from 'types';

const BASE_URL = new URL('/api/transactions', window.location.origin);

const isTransaction = (json?: any): json is Transaction => typeof json === 'object' && 'id' in json;
const isTransactionList = (json?: any): json is Transaction[] =>
  typeof json === 'object' && 0 in json && isTransaction(json[0]);

/**
 * How the follow-up damage/image/tire uploads went on the paths that still fan out.
 *
 * Only the bulk path does now. The single path sends everything in one request and the server
 * commits it or nothing, so a partial result is no longer reachable there — see
 * `postTransaction`. Bulk keeps fanning out because one bulk payload creates N transactions that
 * each need the same photos, which the API cannot yet express in a single body.
 */
export type AttachmentOutcome = { attempted: number; failed: number };

/**
 * The outcome of a save, carrying whether re-sending the *identical* request could plausibly work.
 *
 * The distinction is the difference between a useful retry and a loop. A dropped connection or a
 * 5xx may succeed on a second attempt; `Unit ABCU1234567 already in yard` or `Container transaction
 * must include chassis` never will, however many times it is sent. Offering retry for those would
 * leave a gate operator alternating between a button that cannot help and one that discards their
 * work.
 */
export type TransactionSaveOutcome =
  | { ok: true; transaction: Transaction }
  | { ok: false; retryable: boolean };

export type BulkTransactionSaveOutcome =
  | { ok: true; transactions: Transaction[]; attachments: AttachmentOutcome }
  | { ok: false; retryable: boolean };

/**
 * Whether the same request, sent again unchanged, has any chance of a different answer.
 *
 * 4xx describes something wrong with what was sent, and the server checks a great deal the browser
 * cannot know in advance — whether a unit is already in the yard, whether a booking has expired,
 * whether the trucking company is permitted. Those need the operator to change something, not to
 * try harder. Timeouts, rate limits and 5xx are the ones worth retrying.
 */
const isRetryableStatus = (status: number): boolean => status >= 500 || status === 408 || status === 429;

/**
 * `fetch` only rejects on a network-level failure, so a settled-but-not-ok response is a failure
 * too. Counting rejections alone would miss every 4xx and 5xx.
 */
const summarizeAttachments = (results: PromiseSettledResult<Response>[]): AttachmentOutcome => ({
  attempted: results.length,
  failed: results.filter(result => result.status === 'rejected' || !result.value.ok).length
});

/**
 * Sends one request and reports how much of its body has gone out.
 *
 * `fetch` cannot do this: browsers expose no upload-progress signal for it, and a gate save is now
 * a single request carrying every photo. Without progress the operator gets an indefinite spinner
 * for a transfer that can take real seconds, which is the condition behind Rollbar #94's largest
 * burst — nine sockets dying in the same millisecond because someone decided the page had hung and
 * closed the tab. A moving percentage is what makes waiting reasonable.
 *
 * The result is a real `Response`, so everything downstream — `response.ok`, `handleBadResponse`,
 * `json()` — is unchanged.
 */
const sendWithUploadProgress = (
  url: string,
  init: RequestInit,
  onProgress?: (percent: number) => void
): Promise<Response> =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(init.method ?? 'POST', url);

    (init.headers as Headers).forEach((value, key) => request.setRequestHeader(key, value));

    if (onProgress) {
      request.upload.onprogress = event => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
    }

    request.onload = () => {
      /* A zero status is a network-level failure dressed as a load; Response would reject it too. */
      if (!request.status) {
        reject(new TypeError('Failed to fetch'));
        return;
      }
      resolve(new Response(request.responseText, { status: request.status, statusText: request.statusText }));
    };
    request.onerror = () => reject(new TypeError('Failed to fetch'));
    request.onabort = () => reject(new TypeError('Failed to fetch'));

    request.send(init.body as string);
  });

export const useTransactionApi = () => {
  const { handleError, setSuccess } = useResponse();

  const handleBadResponse = useCallback(
    (response: Response, json?: any): null => {
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          handleError(json?.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    },
    [handleError]
  );

  const deleteTransaction = useCallback(
    async (transactionId: number, deleteReason: string): Promise<number | null> => {
      const DELETE_INIT: RequestInit = {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'text/plain',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: deleteReason
      };
      const response = await fetch(`${BASE_URL}/${transactionId}`, DELETE_INIT);
      if (response.ok) {
        setSuccess(`Transaction ${transactionId} deleted.`);
        return transactionId;
      }
      return handleBadResponse(response, await response.json());
    },
    [handleBadResponse, setSuccess]
  );

  const editTransaction = useCallback(
    async ({
      images,
      primaryChassis,
      primaryContainer,
      primaryGenset,
      primaryUnit,
      billableEvents,
      ...transaction
    }: Transaction): Promise<Transaction | null> => {
      const PUT_INIT: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(transaction)
      };

      const response = await fetch(`${BASE_URL}/${transaction.id}`, PUT_INIT);

      const json = await response.json();

      if (response.ok) {
        setSuccess(`Transaction #${transaction.id} updated.`);
        return json;
      }

      return handleBadResponse(response, json);
    },
    [handleBadResponse, setSuccess]
  );

  /**
   * `idempotencyKey`, when given, lets the server recognise a retry of this same save attempt. It
   * closes the one gap the server's all-or-nothing boundary cannot: the save committed and the
   * response never arrived, which from here is indistinguishable from a failure.
   */
  const getPostInit = useCallback(
    (obj: any, idempotencyKey?: string): RequestInit => ({
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {})
      }),
      body: JSON.stringify(obj)
    }),
    []
  );

  /**
   * Bulk still fans its images out, and so can still end up partially saved.
   *
   * This is deliberate rather than an oversight. One bulk payload creates N transactions that each
   * need the same photos, and the API blanks `damages`/`images`/`tires` on the bulk path precisely
   * because of that — so there is no single body that could carry them. Folding bulk in needs a
   * backend change and is tracked separately; until then bulk keeps the partial-failure reporting
   * that the single path no longer needs.
   */
  const postBulkTransaction = useCallback(
    async (
      { damages, images, tires, ...transaction }: Transaction,
      unstack: boolean
    ): Promise<BulkTransactionSaveOutcome> => {
      try {
        const response = await fetch(`${BASE_URL.toString()}/bulk?unstack=${unstack}`, getPostInit(transaction));
        const json = await response.json();

        if (response.ok && isTransactionList(json)) {
          const promises: Promise<Response>[] = images.reduce(
            (acc, img) =>
              acc.concat(json.map(({ id }) => fetch(`${BASE_URL.toString()}/${id}/images`, getPostInit(img)))),
            [] as Promise<Response>[]
          );

          const attachments = summarizeAttachments(await Promise.allSettled(promises));

          /* Only claim success when every attachment landed; the caller reports the partial case. */
          if (!attachments.failed) setSuccess(`Transactions ${json.map(({ id }) => id).join(', ')} created!`);
          return { ok: true, transactions: json, attachments };
        }

        handleBadResponse(response, json);
        return { ok: false, retryable: isRetryableStatus(response.status) };
      } catch (e) {
        handleError(e);
        return { ok: false, retryable: true };
      }
    },
    [getPostInit, handleBadResponse, handleError, setSuccess]
  );

  /**
   * Saves a transaction and its attachments in **one** request.
   *
   * The damages, images and tire inspections used to be stripped out here and re-posted as separate
   * parallel requests once the parent had been created. That is what Rollbar #94 was: the parent
   * was already committed by the time those ran, so anything that killed them — a closed tab, a
   * dropped connection, a 500 — left a gate move in the database without its damage photos, and
   * `Promise.allSettled` swallowed the evidence. A follow-up request cannot join the transaction
   * that created the parent, so as long as attachments arrived separately, "saved or not saved at
   * all" was unachievable no matter what this hook reported.
   *
   * The backend already accepted all three collections on this body; the UI was opting out of a
   * capability the API had.
   */
  const postTransaction = useCallback(
    async (
      transaction: Transaction,
      unstack?: boolean,
      idempotencyKey?: string,
      onProgress?: (percent: number) => void
    ): Promise<TransactionSaveOutcome> => {
      const transactionInput: string | URL = !!transaction.draft
        ? BASE_URL.toString() + '/drafts' /* save or update (replace) draft */
        : !!transaction.id
        ? BASE_URL.toString() + `/drafts/${transaction.id}` /* complete draft */
        : BASE_URL; /* save transaction */

      const transactionInit: RequestInit = {
        ...getPostInit(transaction, idempotencyKey),
        method: !transaction.draft && !!transaction.id ? 'PUT' : 'POST' /* PUT only for complete draft */
      };

      try {
        const response = await sendWithUploadProgress(
          transactionInput + (!!unstack ? '?unstack=true' : '?unstack=false'),
          transactionInit,
          onProgress
        );
        const json = await response.json();

        if (response.ok && isTransaction(json)) {
          setSuccess(`${!json.draft ? 'Transaction' : 'Draft'} ${json.id} created!`);
          return { ok: true, transaction: json };
        }

        handleBadResponse(response, json);
        return { ok: false, retryable: isRetryableStatus(response.status) };
      } catch (e) {
        /* The request never completed — a dropped connection, an aborted upload. Worth retrying. */
        handleError(e);
        return { ok: false, retryable: true };
      }
    },
    [getPostInit, handleBadResponse, handleError, setSuccess]
  );

  const sendInterchange = useCallback(async (email: string, transactionId: number) => {
    const POST_INIT: RequestInit = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'text/plain',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }),
      body: email
    };

    await fetch(`${BASE_URL}/${transactionId}/send-interchange`, POST_INIT);
  }, []);

  return { deleteTransaction, editTransaction, postBulkTransaction, postTransaction, sendInterchange };
};
